import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { HomeBrandChip } from './HomeBrandChip';
import { HomeSectionTitle } from './HomeSectionTitle';

interface HomeBrandsSectionProps {
  brands: [string, number][];
  buttonColor: string;
  titleColor: string;
}

export function HomeBrandsSection({ brands, buttonColor, titleColor }: HomeBrandsSectionProps) {
  const brandScrollRef = useRef<HTMLDivElement>(null);
  const [showBrandNavigation, setShowBrandNavigation] = useState(false);

  const scrollBrands = (direction: 'prev' | 'next') => {
    const container = brandScrollRef.current;
    if (!container) return;
    const scrollAmount = Math.max(container.clientWidth * 0.7, 200);
    container.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const checkBrandsOverflow = () => {
      requestAnimationFrame(() => {
        if (brandScrollRef.current) {
          const hasOverflow =
            brandScrollRef.current.scrollWidth > brandScrollRef.current.clientWidth;
          setShowBrandNavigation(hasOverflow);
        }
      });
    };

    const timeoutId = setTimeout(checkBrandsOverflow, 0);

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkBrandsOverflow, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [brands]);

  if (brands.length === 0) return null;

  return (
    <section className="wl5-av-brands wl5-av-brands--premium">
      <div className="wl5-container">
        <HomeSectionTitle
          eyebrow="Marcas"
          titleBefore="Veja as ofertas por"
          titleAccent="montadoras"
          accentColor={buttonColor}
          titleColor={titleColor}
        />

        <div className={`wl5-av-brands-carousel${showBrandNavigation ? ' is-overflow' : ''}`}>
          {showBrandNavigation && (
            <button
              type="button"
              className="wl5-av-brands-carousel-btn"
              onClick={() => scrollBrands('prev')}
              aria-label="Marcas anteriores"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}

          <div
            ref={brandScrollRef}
            className="wl5-av-brands-scroll"
            style={{ justifyContent: showBrandNavigation ? 'flex-start' : 'center' }}
          >
            {brands.map(([brand, count]) => (
              <HomeBrandChip
                key={brand}
                brand={brand}
                count={count}
                accentColor={buttonColor}
                logoOnly
              />
            ))}
          </div>

          {showBrandNavigation && (
            <button
              type="button"
              className="wl5-av-brands-carousel-btn"
              onClick={() => scrollBrands('next')}
              aria-label="Próximas marcas"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
